import Microphone from '@components/icons/FileMicrophone.svg';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from '@modals/UnlockJobModal/styles/Content';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { EErrorMessages } from 'app/types/errorMessages';
import { formatCentsPrice } from '@helpers/formatCentsPrice';
import { formatFileSize } from '@helpers/formatFileSize';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { BillingService, JobPartialUnlockedFilesResponseDto, JobsService, PayService } from 'app/API';
import type { JobPartialUnlockedSourceAudioDto } from 'app/API/models';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ChoiceOptionIcon from '@components/icons/ChoiceOption.svg';
import DownloadFileIcon from '@components/icons/DownloadFile.svg';
import SummaryIcon from '@components/icons/FileDocument.svg';
import { FileSection } from './styles/FileSection';
import { UnlockChoiceOption } from './styles/UnlockChoiceOption';
import { TProps } from './types/TProps';

export function UnlockJobModal({ id, name, onSuccess, onCancel }: TProps) {
  const { closeModal, openModal } = useModalContext();
  const { call } = useAPI();
  const [isLoading, setIsLoading] = useState(true);
  const [partialUnlockProcessed, setPartialUnlockProcessed] = useState(false);
  const [outputUrl, setOutputUrl] = useState('');
  const [summaryUrl, setSummaryUrl] = useState('');
  const [reporterNotesTxtUrl, setReporterNotesTxtUrl] = useState('');
  const [reporterNotesRtfUrl, setReporterNotesRtfUrl] = useState('');
  const [outputFileSize, setOutputFileSize] = useState(0);
  const [summaryFileSize, setSummaryFileSize] = useState(0);
  const [reporterNotesTxtFileSize, setReporterNotesTxtFileSize] = useState(0);
  const [reporterNotesRtfFileSize, setReporterNotesRtfFileSize] = useState(0);
  const [unlockPrice, setUnlockPrice] = useState(0);
  const [error, setError] = useState(false);
  const [partialUnlockRate, setPartialUnlockRate] = useState(0);
  const [sourceAudioFiles, setSourceAudioFiles] = useState<JobPartialUnlockedSourceAudioDto[]>([]);

  const getDownloadUrls = async (interval?: NodeJS.Timeout, loadingToastId?: string) => {
    const onError = () => {
      toast.error(EErrorMessages.DEFAULT);
      setIsLoading(false);
      setError(true);

      if (interval) {
        clearInterval(interval);
      }

      if (loadingToastId) {
        toast.remove(loadingToastId);
      }
    };

    let isSummaryCompleted = false;
    const onSuccess = (response: JobPartialUnlockedFilesResponseDto) => {
      const {
        downloadOutputUrl,
        downloadSummaryUrl,
        downloadOutputFileSize,
        downloadSummaryFileSize,
        downloadReporterNotesTxt,
        downloadReporterNotesRtf,
        downloadReporterNotesTxtFileSize,
        downloadReporterNotesRtfFileSize,
        error,
        sourceAudioFiles: responseSourceAudioFiles,
      } = response;

      if (error) {
        onError();
        isSummaryCompleted = true;
      }

      if (downloadOutputUrl && downloadSummaryUrl) {
        setOutputUrl(downloadOutputUrl);
        setSummaryUrl(downloadSummaryUrl);
        setReporterNotesTxtUrl(downloadReporterNotesTxt);
        setReporterNotesRtfUrl(downloadReporterNotesRtf);
        setOutputFileSize(downloadOutputFileSize || 0);
        setSummaryFileSize(downloadSummaryFileSize || 0);
        setSourceAudioFiles(responseSourceAudioFiles || []);
        setReporterNotesRtfFileSize(downloadReporterNotesRtfFileSize || 0);
        setReporterNotesTxtFileSize(downloadReporterNotesTxtFileSize || 0);
        setIsLoading(false);
        if (interval) {
          clearInterval(interval);
        }
        if (loadingToastId) {
          toast.remove(loadingToastId);
          toast.success('Reporter unlock has been successfully processed');
        }
        setError(false);
        isSummaryCompleted = true;
      }
    };

    await call(JobsService.getJobPartialUnlockedFiles({ jobId: id }), {
      onError,
      onSuccess,
    });

    return isSummaryCompleted;
  };

  useEffect(() => {
    setIsLoading(true);
    const getPrice = async () => {
      let priceErrorMessage = '';
      try {
        const priceData = await call(() => BillingService.calculateJobUnlockPricing({ jobId: id }), {
          onError: (message: string) => {
            priceErrorMessage = message;
          },
        });
        if (priceData?.result) {
          setUnlockPrice(Number(priceData?.result.amount));
          setPartialUnlockRate(Number(priceData?.result.partialUnlockRate));
          setPartialUnlockProcessed(priceData?.result.partialUnlocked);
        }
      } catch {
        toast.error(priceErrorMessage || EErrorMessages.DEFAULT);
        onCancel();
      }
    };

    getPrice();

    const pullDownloadLinks = async () => {
      setIsLoading(true);
      const summaryCompleted = await getDownloadUrls();

      if (!summaryCompleted) {
        const loadingToastId = toast.loading('Waiting for the summary generation...');
        const interval: NodeJS.Timeout = setInterval(async () => await getDownloadUrls(interval, loadingToastId), 5000);

        return () => {
          clearInterval(interval);
          setIsLoading(false);
        };
      }
    };

    if (partialUnlockProcessed) {
      pullDownloadLinks();
    } else {
      setIsLoading(false);
    }
  }, [partialUnlockProcessed]);

  return (
    <ModalWrapper width="460px">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Unlock The Job</ModalTitle>

      <Content>
        <UnlockChoiceOption
          onClick={() => {
            closeModal();
            openModal(ModalNamesEnum.UnlockJobFull, { id, name, onSuccess, onCancel });
          }}
        >
          <div>
            <div className="title">Full unlock</div>
            <div className="description">Get full access to the job</div>
            <div className="price">{formatCentsPrice(unlockPrice || 0)}</div>
          </div>
          <ChoiceOptionIcon />
        </UnlockChoiceOption>

        {partialUnlockProcessed ? (
          <div className="mt-2">
            {error ? (
              <div className="text-red-500 text-sm mt-2 w-max[100px]">
                <div>Something went wrong while retrieving the summary.</div>
                <div>Please contact support.</div>
              </div>
            ) : (
              <>
                <FileSection>
                  <SummaryIcon className="icon" />
                  <div className="file-description">
                    <div>Court_Reporting_Summary.PDF</div>
                    <div>File size {formatFileSize(summaryFileSize)}</div>
                  </div>
                  <a href={summaryUrl} target="_blank" rel="noreferrer">
                    <DownloadFileIcon />
                  </a>
                </FileSection>

                <FileSection className="mt-2">
                  <Microphone className="icon" />
                  <div className="file-description">
                    <div>Court_Reporting_Audio.MP3</div>
                    <div>File size {formatFileSize(outputFileSize)}</div>
                  </div>
                  <a href={outputUrl} target="_blank" rel="noreferrer">
                    <DownloadFileIcon />
                  </a>
                </FileSection>

                {!!reporterNotesTxtUrl && (
                  <FileSection className="mt-2">
                    <SummaryIcon className="icon" />
                    <div className="file-description">
                      <div>Reporter Notes TXT file</div>
                      <div>File size {formatFileSize(reporterNotesTxtFileSize)}</div>
                    </div>
                    <a href={reporterNotesTxtUrl} target="_blank" rel="noreferrer">
                      <DownloadFileIcon />
                    </a>
                  </FileSection>
                )}

                {!!reporterNotesRtfUrl && (
                  <FileSection className="mt-2">
                    <SummaryIcon className="icon" />
                    <div className="file-description">
                      <div>Reporter Notes RTF file</div>
                      <div>File size {formatFileSize(reporterNotesRtfFileSize)}</div>
                    </div>
                    <a href={reporterNotesRtfUrl} target="_blank" rel="noreferrer">
                      <DownloadFileIcon />
                    </a>
                  </FileSection>
                )}

                {sourceAudioFiles.length > 0 ? (
                  <>
                    <div className="mt-3 mb-1 text-xs font-semibold text-[var(--color-text-muted)]">Source Audio</div>
                    {sourceAudioFiles.map((sourceFile) => (
                      <FileSection key={sourceFile.fileName} className="mt-2">
                        <Microphone className="icon" />
                        <div className="file-description">
                          <div>{sourceFile.fileName}</div>
                          {sourceFile.fileSize ? <div>File size {formatFileSize(sourceFile.fileSize)}</div> : null}
                        </div>
                        <a href={sourceFile.downloadUrl} target="_blank" rel="noreferrer">
                          <DownloadFileIcon />
                        </a>
                      </FileSection>
                    ))}
                  </>
                ) : null}

                <div className="mt-3 mb-1 text-xs font-semibold text-[var(--color-text-muted)]">
                  All files ZIP Archive
                </div>

                <FileSection key={`${name}_reporter_unlock_files.zip`} className="mt-2">
                  <SummaryIcon className="icon" />
                  <div className="file-description">
                    <div>{`${name}_reporter_unlock_files.zip`}</div>
                    {/* {sourceFile.fileSize ? <div>File size {formatFileSize(sourceFile.fileSize)}</div> : null} */}
                  </div>
                  <a
                    href={`${window.location.origin}/api/jobs/${id}/partial-unlocked-files/download-zip`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <DownloadFileIcon />
                  </a>
                </FileSection>
              </>
            )}
          </div>
        ) : (
          <UnlockChoiceOption
            className="mt-2"
            onClick={() => {
              call(PayService.partialCharge({ jobId: id }), {
                onError: (message: string) => toast.error(message),
                onSuccess: () => {
                  setPartialUnlockProcessed(true);
                },
              });
            }}
          >
            <div>
              <div className="title">Reporter unlock</div>
              <div className="description">Get summary and audio access</div>
              <div className="price">{formatCentsPrice(partialUnlockRate)}</div>
            </div>
            <ChoiceOptionIcon />
          </UnlockChoiceOption>
        )}

        <div className="buttonWrapper self-end">
          <OutlinedButton onClick={closeModal}>Cancel</OutlinedButton>
        </div>
      </Content>
    </ModalWrapper>
  );
}
